import _objectWithoutPropertiesLoose from '@babel/runtime/helpers/objectWithoutPropertiesLoose';
import _extends from '@babel/runtime/helpers/extends';
import * as React from 'react';
import { StyleSheet } from 'aphrodite';
import { withRouter, Link } from 'react-router-dom';
import { __RouterContext } from 'react-router';
import { addStyle } from '@khanacademy/wonder-blocks-core';
import Color from '@khanacademy/wonder-blocks-color';

const getAppropriateTriggersForRole = role => {
  switch (role) {
    case "link":
      return {
        triggerOnEnter: true,
        triggerOnSpace: false
      };

    case "checkbox":
    case "radio":
    case "listbox":
    case "option":
      return {
        triggerOnEnter: false,
        triggerOnSpace: true
      };

    case "button":
    case "menuitem":
    case "menu":
    default:
      return {
        triggerOnEnter: true,
        triggerOnSpace: true
      };
  }
};

const disabledHandlers = {
  onClick: () => void 0,
  onMouseEnter: () => void 0,
  onMouseLeave: () => void 0,
  onMouseDown: () => void 0,
  onMouseUp: () => void 0,
  onDragStart: () => void 0,
  onTouchStart: () => void 0,
  onTouchEnd: () => void 0,
  onTouchCancel: () => void 0,
  onKeyDown: () => void 0,
  onKeyUp: () => void 0,
  tabIndex: 0
};
const keyCodes = {
  enter: 13,
  space: 32
};
const startState = {
  hovered: false,
  focused: false,
  pressed: false,
  waiting: false
};
class ClickableBehavior extends React.Component {
  static getDerivedStateFromProps(props, state) {
    if (props.disabled) {
      return _extends({}, startState, {
        focused: state.focused
      });
    } else {
      return null;
    }
  }

  constructor(props) {
    super(props);

    this.handleClick = e => {
      const {
        onClick = undefined,
        beforeNav = undefined,
        safeWithNav = undefined
      } = this.props;

      if (this.enterClick) {
        return;
      }

      if (onClick || beforeNav || safeWithNav) {
        this.waitingForClick = false;
      }

      this.runCallbackAndMaybeNavigate(e);
    };

    this.handleMouseEnter = e => {
      if (e.buttons === 1) {
        this.dragging = true;
        this.setState({
          pressed: true
        });
      } else if (!this.waitingForClick) {
        this.setState({
          hovered: true
        });
      }
    };

    this.handleMouseLeave = () => {
      if (!this.waitingForClick) {
        this.dragging = false;
        this.setState({
          hovered: false,
          pressed: false,
          focused: false
        });
      }
    };

    this.handleMouseDown = () => {
      this.setState({
        pressed: true
      });
    };

    this.handleMouseUp = e => {
      if (this.dragging) {
        this.dragging = false;
        this.handleClick(e);
      }

      this.setState({
        pressed: false,
        focused: false
      });
    };

    this.handleDragStart = e => {
      this.dragging = true;
      e.preventDefault();
    };

    this.handleTouchStart = () => {
      this.setState({
        pressed: true
      });
    };

    this.handleTouchEnd = () => {
      this.setState({
        pressed: false
      });
      this.waitingForClick = true;
    };

    this.handleTouchCancel = () => {
      this.setState({
        pressed: false
      });
      this.waitingForClick = true;
    };

    this.handleKeyDown = e => {
      const {
        onKeyDown,
        role
      } = this.props;

      if (onKeyDown) {
        onKeyDown(e);
      }

      const keyCode = e.which || e.keyCode;
      const {
        triggerOnEnter,
        triggerOnSpace
      } = getAppropriateTriggersForRole(role);

      if (triggerOnEnter && keyCode === keyCodes.enter || triggerOnSpace && keyCode === keyCodes.space) {
        e.preventDefault();
        this.setState({
          pressed: true
        });
      } else if (!triggerOnEnter && keyCode === keyCodes.enter) {
        this.enterClick = true;
      }
    };

    this.handleKeyUp = e => {
      const {
        onKeyUp,
        role
      } = this.props;

      if (onKeyUp) {
        onKeyUp(e);
      }

      const keyCode = e.which || e.keyCode;
      const {
        triggerOnEnter,
        triggerOnSpace
      } = getAppropriateTriggersForRole(role);

      if (triggerOnEnter && keyCode === keyCodes.enter || triggerOnSpace && keyCode === keyCodes.space) {
        this.setState({
          pressed: false,
          focused: true
        });
        this.runCallbackAndMaybeNavigate(e);
      } else if (!triggerOnEnter && keyCode === keyCodes.enter) {
        this.enterClick = false;
      }
    };

    this.handleFocus = e => {
      this.setState({
        focused: true
      });
    };

    this.handleBlur = e => {
      this.setState({
        focused: false,
        pressed: false
      });
    };

    this.state = startState;
    this.waitingForClick = false;
    this.enterClick = false;
    this.dragging = false;
  }

  navigateOrReset(shouldNavigate) {
    if (shouldNavigate) {
      const {
        history,
        href,
        skipClientNav,
        target = undefined
      } = this.props;

      if (href) {
        if (target === "_blank") {
          window.open(href, "_blank");
          this.setState({
            waiting: false
          });
        } else if (history && !skipClientNav) {
          history.push(href);
          this.setState({
            waiting: false
          });
        } else {
          window.location.assign(href);
        }
      }
    } else {
      this.setState({
        waiting: false
      });
    }
  }

  handleSafeWithNav(safeWithNav, shouldNavigate) {
    const {
      skipClientNav,
      history
    } = this.props;

    if (history && !skipClientNav || this.props.target === "_blank") {
      safeWithNav();
      this.navigateOrReset(shouldNavigate);
      return Promise.resolve();
    } else {
      if (!this.state.waiting) {
        this.setState({
          waiting: true
        });
      }

      return safeWithNav().then(() => {
        if (!this.state.waiting) {
          this.setState({
            waiting: true
          });
        }

        return;
      }).catch(error => {}).finally(() => {
        this.navigateOrReset(shouldNavigate);
      });
    }
  }

  runCallbackAndMaybeNavigate(e) {
    const {
      onClick = undefined,
      beforeNav = undefined,
      safeWithNav = undefined,
      href,
      type
    } = this.props;
    let shouldNavigate = true;
    let canSubmit = true;

    if (onClick) {
      onClick(e);
    }

    if (e.defaultPrevented) {
      shouldNavigate = false;
      canSubmit = false;
    }

    e.preventDefault();

    if (!href && type === "submit" && canSubmit) {
      let target = e.currentTarget;

      while (target) {
        if (target instanceof window.HTMLFormElement) {
          const event = new window.Event("submit", {
            cancelable: true
          });
          target.dispatchEvent(event);
          break;
        }

        target = target.parentElement;
      }
    }

    if (beforeNav) {
      this.setState({
        waiting: true
      });
      beforeNav().then(() => {
        if (safeWithNav) {
          return this.handleSafeWithNav(safeWithNav, shouldNavigate);
        } else {
          return this.navigateOrReset(shouldNavigate);
        }
      }).catch(() => {});
    } else if (safeWithNav) {
      return this.handleSafeWithNav(safeWithNav, shouldNavigate);
    } else {
      this.navigateOrReset(shouldNavigate);
    }
  }

  render() {
    const childrenProps = this.props.disabled ? _extends({}, disabledHandlers, {
      onFocus: this.handleFocus,
      onBlur: this.handleBlur
    }) : {
      onClick: this.handleClick,
      onMouseEnter: this.handleMouseEnter,
      onMouseLeave: this.handleMouseLeave,
      onMouseDown: this.handleMouseDown,
      onMouseUp: this.handleMouseUp,
      onDragStart: this.handleDragStart,
      onTouchStart: this.handleTouchStart,
      onTouchEnd: this.handleTouchEnd,
      onTouchCancel: this.handleTouchCancel,
      onKeyDown: this.handleKeyDown,
      onKeyUp: this.handleKeyUp,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      tabIndex: 0
    };
    childrenProps.rel = this.props.rel || (this.props.target === "_blank" ? "noopener noreferrer" : undefined);
    const {
      children
    } = this.props;
    return children && children(this.state, childrenProps);
  }

}
ClickableBehavior.defaultProps = {
  disabled: false
};

const isClientSideUrl = href => {
  if (typeof href !== "string") {
    return false;
  }

  return !/^(https?:)?\/\//i.test(href) && !/^([^#]*#[\w-]*|[\w\-.]+:)/.test(href);
};

const ClickableBehaviorWithRouter = withRouter(ClickableBehavior);
function getClickableBehavior(href, skipClientNav, router) {
  if (router && skipClientNav !== true && href && isClientSideUrl(href)) {
    return ClickableBehaviorWithRouter;
  }

  return ClickableBehavior;
}

const _excluded = ["href", "onClick", "skipClientNav", "beforeNav", "safeWithNav", "style", "target", "testId", "onKeyDown", "onKeyUp", "hideDefaultFocusRing", "light", "disabled"];
const StyledAnchor = addStyle("a");
const StyledButton = addStyle("button");
const StyledLink = addStyle(Link);
class Clickable extends React.Component {
  constructor(...args) {
    super(...args);

    this.getCorrectTag = (clickableState, router, commonProps) => {
      const activeHref = this.props.href && !this.props.disabled;
      const useClient = router && !this.props.skipClientNav && isClientSideUrl(this.props.href || "");

      if (activeHref && useClient && this.props.href) {
        return React.createElement(StyledLink, _extends({}, commonProps, {
          to: this.props.href,
          role: this.props.role,
          target: this.props.target || undefined,
          "aria-disabled": this.props.disabled ? "true" : undefined
        }), this.props.children(clickableState));
      } else if (activeHref && !useClient) {
        return React.createElement(StyledAnchor, _extends({}, commonProps, {
          href: this.props.href,
          role: this.props.role,
          target: this.props.target || undefined,
          "aria-disabled": this.props.disabled ? "true" : undefined
        }), this.props.children(clickableState));
      } else {
        return React.createElement(StyledButton, _extends({}, commonProps, {
          type: "button",
          "aria-disabled": this.props.disabled
        }), this.props.children(clickableState));
      }
    };
  }

  renderClickableBehavior(router) {
    const _this$props = this.props,
          {
      href,
      onClick,
      skipClientNav,
      beforeNav = undefined,
      safeWithNav = undefined,
      style,
      target = undefined,
      testId,
      onKeyDown,
      onKeyUp,
      hideDefaultFocusRing,
      light,
      disabled
    } = _this$props,
          restProps = _objectWithoutPropertiesLoose(_this$props, _excluded);

    const ClickableBehavior = getClickableBehavior(href, skipClientNav, router);

    const getStyle = state => [styles.reset, styles.link, !hideDefaultFocusRing && state.focused && (light ? styles.focusedLight : styles.focused), disabled && styles.disabled, style];

    if (beforeNav) {
      return React.createElement(ClickableBehavior, {
        href: href,
        onClick: onClick,
        beforeNav: beforeNav,
        safeWithNav: safeWithNav,
        onKeyDown: onKeyDown,
        onKeyUp: onKeyUp,
        disabled: disabled
      }, (state, childrenProps) => this.getCorrectTag(state, router, _extends({}, restProps, {
        "data-test-id": testId,
        style: getStyle(state)
      }, childrenProps)));
    } else {
      return React.createElement(ClickableBehavior, {
        href: href,
        onClick: onClick,
        safeWithNav: safeWithNav,
        onKeyDown: onKeyDown,
        onKeyUp: onKeyUp,
        target: target,
        disabled: disabled
      }, (state, childrenProps) => this.getCorrectTag(state, router, _extends({}, restProps, {
        "data-test-id": testId,
        style: getStyle(state)
      }, childrenProps)));
    }
  }

  render() {
    return React.createElement(__RouterContext.Consumer, null, router => this.renderClickableBehavior(router));
  }

}
Clickable.defaultProps = {
  light: false,
  disabled: false,
  "aria-label": ""
};
const styles = StyleSheet.create({
  reset: {
    border: "none",
    margin: 0,
    padding: 0,
    width: "auto",
    overflow: "visible",
    background: "transparent",
    textDecoration: "none",
    color: "inherit",
    font: "inherit",
    boxSizing: "border-box",
    touchAction: "manipulation",
    userSelect: "none",
    outline: "none",
    lineHeight: "normal",
    WebkitFontSmoothing: "inherit",
    MozOsxFontSmoothing: "inherit"
  },
  link: {
    cursor: "pointer"
  },
  focused: {
    ":focus": {
      outline: `solid 2px ${Color.blue}`
    }
  },
  focusedLight: {
    outline: `solid 2px ${Color.white}`
  },
  disabled: {
    color: Color.offBlack32,
    cursor: "not-allowed",
    ":focus": {
      outline: "none"
    },
    ":focus-visible": {
      outline: `solid 2px ${Color.blue}`
    }
  }
});

export { ClickableBehavior, Clickable as default, getClickableBehavior, isClientSideUrl };
